import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Calendar, RefreshCw, Loader, Ban, Clock, Mail, Phone, User,
  ChevronLeft, ChevronRight, Plus, X, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isoDate = (d) => d.toISOString().slice(0, 10);
const formatDateLong = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
};
const formatMonthYear = (iso) => {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const slotLabel = (s) => {
  const [h, m] = s.split(':');
  const hi = parseInt(h, 10);
  const period = hi < 12 ? 'AM' : 'PM';
  const h12 = hi === 0 ? 12 : (hi > 12 ? hi - 12 : hi);
  return `${h12}:${m} ${period}`;
};

const startOfWeek = (iso) => {
  const d = new Date(iso + 'T12:00:00');
  const dow = d.getDay(); // 0..6 (Sun..Sat)
  d.setDate(d.getDate() - dow);
  return isoDate(d);
};
const addDays = (iso, n) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return isoDate(d);
};
const startOfMonth = (iso) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(1);
  return isoDate(d);
};
const lastDayOfMonth = (iso) => {
  const d = new Date(iso + 'T12:00:00');
  d.setMonth(d.getMonth() + 1, 0);
  return isoDate(d);
};

const PAYMENT_TYPES = [
  { value: 'cash',   label: 'Cash' },
  { value: 'card',   label: 'Card (in-person)' },
  { value: 'comp',   label: 'Complimentary' },
  { value: 'credit', label: 'Session Credit' },
];

/* ── Manual Booking Modal ─────────────────────────────────────── */
const ManualBookingModal = ({ onClose, onSaved }) => {
  const today = isoDate(new Date());
  const [form, setForm] = useState({
    first_name: '', email: '', phone: '',
    booking_date: today, slot: '',
    payment_type: 'cash', amount: '', notes: '',
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!form.booking_date) return;
    setLoadingSlots(true);
    setForm(f => ({ ...f, slot: '' }));
    axios.get(`${API}/bookings/availability?date=${form.booking_date}`)
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [form.booking_date]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim()) return setError('Customer name is required.');
    if (!form.slot) return setError('Please select a time slot.');
    setSaving(true);
    try {
      await axios.post(`${API}/bookings/staff/manual`, {
        ...form,
        amount: parseFloat(form.amount) || 0,
      });
      toast.success('Booking created!');
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create booking.');
    } finally {
      setSaving(false);
    }
  };

  const inp = 'w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-[#1f3d2b] bg-white';
  const lbl = 'block text-xs font-bold uppercase tracking-[0.15em] text-gray-500 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm px-0 sm:px-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>New Manual Booking</h3>
            <p className="text-xs text-gray-500 mt-0.5">Walk-in, cash, or phone booking</p>
          </div>
          <button data-testid="manual-booking-close" onClick={onClose} className="p-2 rounded-full text-gray-400 hover:bg-gray-100 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className={lbl}>Customer Name *</label>
            <input data-testid="manual-booking-name" className={inp} placeholder="First name"
              value={form.first_name} onChange={e => set('first_name', e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Email</label>
              <input data-testid="manual-booking-email" className={inp} type="email" placeholder="optional"
                value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div>
              <label className={lbl}>Phone</label>
              <input data-testid="manual-booking-phone" className={inp} placeholder="optional"
                value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}><Calendar size={10} className="inline mr-1" />Date *</label>
              <input data-testid="manual-booking-date" className={inp} type="date" min={today}
                value={form.booking_date} onChange={e => set('booking_date', e.target.value)} required />
            </div>
            <div>
              <label className={lbl}><Clock size={10} className="inline mr-1" />Time *</label>
              {loadingSlots
                ? <div className="text-xs text-gray-400 pt-2">Loading…</div>
                : (
                  <select data-testid="manual-booking-slot" className={inp}
                    value={form.slot} onChange={e => set('slot', e.target.value)} required>
                    <option value="">— pick time —</option>
                    {slots.map(s => (
                      <option key={s.slot} value={s.slot} disabled={!s.available}>
                        {s.label}{!s.available ? ' (taken)' : ''}
                      </option>
                    ))}
                  </select>
                )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Payment Type</label>
              <select data-testid="manual-booking-payment-type" className={inp}
                value={form.payment_type} onChange={e => set('payment_type', e.target.value)}>
                {PAYMENT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Amount ($)</label>
              <input data-testid="manual-booking-amount" className={inp} type="number" min="0" step="0.01"
                placeholder="0.00" value={form.amount} onChange={e => set('amount', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={lbl}>Notes</label>
            <textarea data-testid="manual-booking-notes" className={`${inp} resize-none`} rows={2}
              placeholder="e.g. Walk-in, referred by…" value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</div>}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button type="button" onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button data-testid="manual-booking-submit" onClick={handleSubmit} disabled={saving}
            className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white bg-[#1f3d2b] hover:bg-[#162d1f] transition-all disabled:opacity-60">
            {saving ? 'Saving…' : 'Create Booking'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Day view --------------------------------------------------------------
const DayView = ({ date, isAdmin }) => {
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySlot, setBusySlot] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [bookingsRes, blocksRes, availRes] = await Promise.all([
        axios.get(`${API}/bookings/staff/range`, { params: { from_: date, to: date } })
          .catch(() => ({ data: [] })),
        axios.get(`${API}/bookings/staff/blocks`, { params: { from_: date, to: date } })
          .catch(() => ({ data: [] })),
        axios.get(`${API}/bookings/availability`, { params: { date } }),
      ]);
      setBookings(bookingsRes.data || []);
      setBlocks(blocksRes.data || []);
      setAvailability(availRes.data?.slots || []);
    } catch {
      toast.error('Could not load bookings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [date]);

  const blockedSet = new Set(blocks.map((b) => b.slot));
  const bookingBySlot = Object.fromEntries(bookings.map((b) => [b.slot, b]));

  const toggleBlock = async (slot) => {
    setBusySlot(slot);
    try {
      if (blockedSet.has(slot)) {
        await axios.post(`${API}/bookings/admin/unblock`, { booking_date: date, slot });
        toast.success(`Unblocked ${slotLabel(slot)}`);
      } else {
        await axios.post(`${API}/bookings/admin/block`, { booking_date: date, slot, reason: 'Admin block' });
        toast.success(`Blocked ${slotLabel(slot)}`);
      }
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update slot');
    } finally {
      setBusySlot(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 text-gray-500 py-10">
        <Loader className="animate-spin" size={16} /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {availability.map(({ slot, label, available, reason }) => {
        const booking = bookingBySlot[slot];
        const isBlocked = blockedSet.has(slot);
        const isPast = reason === 'past';
        const isBusy = busySlot === slot;
        return (
          <div
            key={slot}
            data-testid={`booking-row-${slot}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
              booking ? 'bg-green-50 border-green-200'
              : isBlocked ? 'bg-red-50 border-red-200'
              : isPast ? 'bg-gray-50 border-gray-200 opacity-60'
              : 'bg-white border-gray-200'
            }`}
          >
            <div className="w-20 text-sm font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {label}
            </div>
            <div className="flex-1 min-w-0">
              {booking ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="inline-flex items-center gap-1 text-[#0d1f15] font-semibold">
                    <User size={13} /> {booking.first_name}
                  </span>
                  <span className="inline-flex items-center gap-1 text-gray-600">
                    <Phone size={13} /> {booking.phone}
                  </span>
                  <span className="inline-flex items-center gap-1 text-gray-600 truncate">
                    <Mail size={13} /> {booking.email}
                  </span>
                </div>
              ) : isBlocked ? (
                <span className="inline-flex items-center gap-1 text-red-700 text-sm font-semibold">
                  <Ban size={13} /> Blocked
                </span>
              ) : isPast ? (
                <span className="text-sm text-gray-500 inline-flex items-center gap-1">
                  <Clock size={13} /> Past
                </span>
              ) : (
                <span className="text-sm text-gray-500">Available</span>
              )}
            </div>
            {isAdmin && !booking && !isPast && (
              <button
                data-testid={`bookings-toggle-${slot}`}
                onClick={() => toggleBlock(slot)}
                disabled={isBusy}
                className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 ${
                  isBlocked
                    ? 'bg-white border border-red-300 text-red-700 hover:bg-red-50'
                    : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {isBusy ? <Loader className="animate-spin" size={12} /> : isBlocked ? 'Unblock' : 'Block'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---- Week view -------------------------------------------------------------
const WeekView = ({ weekStart }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/bookings/staff/range`, {
      params: { from_: days[0], to: days[6] },
    }).then(({ data }) => { if (!cancelled) setBookings(data || []); })
      .catch(() => { if (!cancelled) setBookings([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [weekStart]);

  const byDay = useMemo(() => {
    const m = {};
    days.forEach((d) => { m[d] = []; });
    bookings.forEach((b) => { (m[b.booking_date] ||= []).push(b); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.slot.localeCompare(b.slot)));
    return m;
  }, [bookings, weekStart]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 text-gray-500 py-10">
        <Loader className="animate-spin" size={16} /> Loading week…
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
      {days.map((iso) => {
        const dt = new Date(iso + 'T12:00:00');
        const todayIso = isoDate(new Date());
        const isToday = iso === todayIso;
        const list = byDay[iso] || [];
        return (
          <div
            key={iso}
            data-testid={`week-day-${iso}`}
            className={`border rounded-xl p-3 min-h-[140px] ${isToday ? 'border-[#1f3d2b]/40 bg-[#1f3d2b]/5' : 'border-gray-200 bg-white'}`}
          >
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
              {dt.toLocaleDateString(undefined, { weekday: 'short' })}
            </div>
            <div className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {dt.getDate()}
            </div>
            <div className="mt-2 space-y-1.5">
              {list.length === 0 ? (
                <div className="text-xs text-gray-400">No bookings</div>
              ) : list.map((b) => (
                <div
                  key={b.id}
                  className="text-xs bg-green-50 border border-green-200 rounded-md px-2 py-1.5 truncate"
                  title={`${b.slot_label} · ${b.first_name}`}
                >
                  <div className="font-bold text-[#0d1f15] truncate">{b.slot_label}</div>
                  <div className="text-gray-700 truncate">{b.first_name}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---- Month view ------------------------------------------------------------
const MonthView = ({ monthStart, onPickDate }) => {
  const lastDay = lastDayOfMonth(monthStart);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/bookings/staff/range`, {
      params: { from_: monthStart, to: lastDay },
    }).then(({ data }) => { if (!cancelled) setBookings(data || []); })
      .catch(() => { if (!cancelled) setBookings([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [monthStart]);

  const countsByDate = useMemo(() => {
    const m = {};
    bookings.forEach((b) => { m[b.booking_date] = (m[b.booking_date] || 0) + 1; });
    return m;
  }, [bookings]);

  // Grid cells: lead with empty cells to align weekday of day-1
  const first = new Date(monthStart + 'T12:00:00');
  const leadingBlanks = first.getDay(); // 0..6 from Sun
  const total = new Date(lastDay + 'T12:00:00').getDate();
  const todayIso = isoDate(new Date());

  return (
    <div>
      <div className="grid grid-cols-7 text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`b-${i}`} />
        ))}
        {Array.from({ length: total }).map((_, i) => {
          const day = i + 1;
          const iso = `${monthStart.slice(0, 8)}${String(day).padStart(2, '0')}`;
          const count = countsByDate[iso] || 0;
          const isToday = iso === todayIso;
          return (
            <button
              key={iso}
              data-testid={`month-day-${iso}`}
              onClick={() => onPickDate(iso)}
              className={`relative aspect-square rounded-lg border text-sm transition-colors flex flex-col items-center justify-start py-1 px-1.5 ${
                isToday ? 'border-[#1f3d2b] bg-[#1f3d2b]/5' : 'border-gray-200 hover:border-[#1f3d2b]/40 hover:bg-[#1f3d2b]/[0.03]'
              }`}
            >
              <span className={`text-xs font-semibold ${isToday ? 'text-[#1f3d2b]' : 'text-gray-700'}`}>
                {day}
              </span>
              {count > 0 && (
                <span className="mt-auto text-[10px] font-bold uppercase tracking-widest text-white bg-[#1f3d2b] rounded-full px-2 py-0.5">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {loading && (
        <div className="text-xs text-gray-400 text-center mt-3">Loading month…</div>
      )}
    </div>
  );
};

// ---- Container -------------------------------------------------------------
const BookingsScreen = () => {
  const { isAdmin } = useAuth();
  const [view, setView] = useState('day'); // 'day' | 'week' | 'month'
  const [date, setDate] = useState(isoDate(new Date()));
  const [refreshTick, setRefreshTick] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // Header labels per view
  const header = useMemo(() => {
    if (view === 'day') return formatDateLong(date);
    if (view === 'week') {
      const ws = startOfWeek(date);
      const we = addDays(ws, 6);
      const a = new Date(ws + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const b = new Date(we + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      return `${a} – ${b}`;
    }
    return formatMonthYear(date);
  }, [view, date]);

  const shift = (delta) => {
    if (view === 'day') setDate(addDays(date, delta));
    else if (view === 'week') setDate(addDays(date, delta * 7));
    else {
      const d = new Date(date + 'T12:00:00');
      d.setMonth(d.getMonth() + delta);
      setDate(isoDate(d));
    }
  };

  const ViewTab = ({ id, label }) => (
    <button
      data-testid={`bookings-view-${id}`}
      onClick={() => setView(id)}
      className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition-colors ${
        view === id ? 'bg-white text-[#1f3d2b] shadow-sm' : 'text-gray-600 hover:text-gray-800'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex justify-between items-start sm:items-center gap-3 flex-wrap mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#1f3d2b]/10 flex items-center justify-center">
              <Calendar className="text-[#1f3d2b]" size={20} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Wellness Bookings
              </h2>
              <p className="text-sm text-gray-600 mt-0.5">
                {isAdmin ? 'Manage availability and view bookings.' : 'See bookings across the team.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <ViewTab id="day" label="Day" />
              <ViewTab id="week" label="Week" />
              <ViewTab id="month" label="Month" />
            </div>
            <button
              data-testid="new-manual-booking-btn"
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-1.5 bg-[#1f3d2b] text-white font-bold rounded-lg px-3 py-2 text-xs uppercase tracking-widest hover:bg-[#162d1f] transition-colors"
            >
              <Plus size={14} /> New
            </button>
            <button
              data-testid="bookings-refresh"
              onClick={() => setRefreshTick((t) => t + 1)}
              className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-semibold rounded-lg px-3 py-2 transition-colors flex items-center gap-2"
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl p-2 mb-5">
          <button
            data-testid="bookings-prev"
            onClick={() => shift(-1)}
            className="p-2 rounded-lg hover:bg-white text-gray-600"
            aria-label="Previous"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="text-center flex-1">
            <div className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {header}
            </div>
            {view === 'day' && (
              <input
                data-testid="bookings-date-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="text-xs text-gray-500 bg-transparent border-none focus:outline-none mt-0.5"
              />
            )}
            {view !== 'day' && (
              <button
                onClick={() => setDate(isoDate(new Date()))}
                className="text-xs text-[#1f3d2b] underline underline-offset-2 mt-0.5"
              >
                Jump to today
              </button>
            )}
          </div>
          <button
            data-testid="bookings-next"
            onClick={() => shift(1)}
            className="p-2 rounded-lg hover:bg-white text-gray-600"
            aria-label="Next"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {view === 'day' && <DayView key={`day-${date}-${refreshTick}`} date={date} isAdmin={isAdmin} />}
        {view === 'week' && <WeekView key={`week-${startOfWeek(date)}-${refreshTick}`} weekStart={startOfWeek(date)} />}
        {view === 'month' && (
          <MonthView
            key={`month-${startOfMonth(date)}-${refreshTick}`}
            monthStart={startOfMonth(date)}
            onPickDate={(iso) => { setDate(iso); setView('day'); }}
          />
        )}
      </div>

      {showModal && (
        <ManualBookingModal
          onClose={() => setShowModal(false)}
          onSaved={() => setRefreshTick(t => t + 1)}
        />
      )}
    </div>
  );
};

export default BookingsScreen;
